import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TimeRangeSelector = ({ onTimeRangeChange, onHistoricalPlayback }) => {
  const [selectedRange, setSelectedRange] = useState('current');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const timeRanges = [
    {
      id: 'current',
      label: 'Current Moment',
      description: 'Real-time planetary positions',
      icon: 'Clock',
      color: 'primary'
    },
    {
      id: 'birth',
      label: 'Birth Chart',
      description: 'Your natal planetary positions',
      icon: 'Star',
      color: 'secondary'
    },
    {
      id: 'today',
      label: 'Today',
      description: 'Full day planetary movement',
      icon: 'Sun',
      color: 'accent'
    },
    {
      id: 'week',
      label: 'This Week',
      description: 'Weekly planetary transitions',
      icon: 'Calendar',
      color: 'success'
    },
    {
      id: 'month',
      label: 'This Month',
      description: 'Monthly astrological patterns',
      icon: 'CalendarDays',
      color: 'warning'
    },
    {
      id: 'year',
      label: 'This Year',
      description: 'Annual planetary cycles',
      icon: 'CalendarRange',
      color: 'error'
    },
    {
      id: 'custom',
      label: 'Custom Range',
      description: 'Select specific time period',
      icon: 'Settings',
      color: 'text-secondary'
    }
  ];

  const significantDates = [
    {
      date: '2024-03-20',
      event: 'Spring Equinox',
      description: 'Sun enters Aries'
    },
    {
      date: '2024-04-08',
      event: 'Solar Eclipse',
      description: 'New Moon in Aries'
    },
    {
      date: '2024-04-23',
      event: 'Full Moon',
      description: 'Moon in Scorpio'
    },
    {
      date: '2024-05-15',
      event: 'Mercury Retrograde',
      description: 'Communication challenges'
    }
  ];

  const handleRangeSelect = (rangeId) => {
    setSelectedRange(rangeId);
    onTimeRangeChange(rangeId);
  };

  const handleCustomRangeApply = () => {
    if (customStartDate && customEndDate) {
      onTimeRangeChange('custom', { start: customStartDate, end: customEndDate });
    }
  };

  const handleHistoricalPlay = (date, event) => {
    onHistoricalPlayback(date, event);
  };

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary':
        return 'bg-primary/20 text-primary border-primary/30';
      case 'secondary':
        return 'bg-secondary/20 text-secondary border-secondary/30';
      case 'accent':
        return 'bg-accent/20 text-accent border-accent/30';
      case 'success':
        return 'bg-success/20 text-success border-success/30';
      case 'warning':
        return 'bg-warning/20 text-warning border-warning/30';
      case 'error':
        return 'bg-error/20 text-error border-error/30';
      default:
        return 'bg-surface/50 text-text-secondary border-border';
    }
  };

  return (
    <div className="w-full glass bg-surface/95 backdrop-blur-cosmic rounded-xl border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
              <Icon name="History" size={16} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary">
                Time Range
              </h3>
              <p className="text-xs text-text-secondary">
                Select time period for planetary audio
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-8 h-8 p-0"
          />
        </div>
      </div>

      {/* Quick Range Selection */}
      <div className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {timeRanges.map((range) => (
            <button
              key={range.id}
              onClick={() => handleRangeSelect(range.id)}
              className={`
                p-3 rounded-lg border transition-cosmic text-left
                ${selectedRange === range.id 
                  ? getColorClasses(range.color)
                  : 'bg-surface/30 text-text-secondary border-border hover:bg-surface/50'
                }
              `}
            >
              <div className="flex items-center space-x-2 mb-1">
                <Icon name={range.icon} size={16} color="currentColor" />
                <span className="text-sm font-medium">{range.label}</span>
              </div>
              <p className="text-xs opacity-80">{range.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Custom Range Inputs */}
      {selectedRange === 'custom' && (
        <div className="px-4 pb-4">
          <div className="glass bg-surface/50 backdrop-blur-cosmic rounded-lg p-4">
            <h4 className="text-sm font-medium text-text-primary mb-3">Custom Time Range</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Start Date</label>
                <Input
                  type="datetime-local"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">End Date</label>
                <Input
                  type="datetime-local"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="primary"
                onClick={handleCustomRangeApply}
                disabled={!customStartDate || !customEndDate}
              >
                Apply Range
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border/50">
          {/* Significant Dates */}
          <div className="p-4">
            <h4 className="text-sm font-medium text-text-primary mb-3">Significant Astrological Dates</h4>
            <div className="space-y-2">
              {significantDates.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-surface/30 rounded-lg hover:bg-surface/50 transition-cosmic"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-text-primary">{item.event}</span>
                      <span className="text-xs text-text-secondary font-mono">{item.date}</span>
                    </div>
                    <p className="text-xs text-text-secondary mt-1">{item.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    iconName="Play"
                    onClick={() => handleHistoricalPlay(item.date, item.event)}
                    className="w-8 h-8 p-0"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-border/50">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                iconName="Sunrise"
                onClick={() => handleRangeSelect('sunrise')}
              >
                Sunrise Today
              </Button>
              <Button
                variant="ghost"
                iconName="Sunset"
                onClick={() => handleRangeSelect('sunset')}
              >
                Sunset Today
              </Button>
              <Button
                variant="ghost"
                iconName="Moon"
                onClick={() => handleRangeSelect('moonphase')}
              >
                Current Moon Phase
              </Button>
              <Button
                variant="ghost"
                iconName="Sparkles"
                onClick={() => handleRangeSelect('retrograde')}
              >
                Retrograde Periods
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;